import React, { useState, useEffect, useRef } from 'react';
import SimplePriceFilter from './SimplePriceFilter';
import { API } from '../services/apiConfig';

const TYPE_LABELS = [
  { key: 'ALL',                  label: 'All'                },
  { key: 'ECO_FRIENDLY',         label: 'Eco-Friendly'       },
  { key: 'STATIONERY',           label: 'Stationery'         },
  { key: 'CORPORATE_ADD_ONS',    label: 'Corporate Add-Ons'  },
  { key: 'BACKPACKS_AND_SLINGS', label: 'Backpacks & Slings' },
  { key: 'TRAVELLING_ADD_ONS',   label: 'Travelling Add-Ons' },
  { key: 'ACCESSORIES',          label: 'Accessories'        },
];

const THEME = {
  'standard':      { active: '#107FDF', faint: 'rgba(16,127,223,0.1)'  },
  'premium':       { active: '#0A683C', faint: 'rgba(10,104,60,0.1)'   },
  'super-premium': { active: '#9A1111', faint: 'rgba(154,17,17,0.1)'   },
};

const PAGE_SIZE = 4;

function ProductSection({ id, title, tagline, titleClass, bgClass = '', onProductClick }) {
  const [allProducts, setAllProducts] = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(false);
  const [ratingsMap, setRatingsMap]   = useState({});
  const [activeType, setActiveType]   = useState('ALL');
  const [page, setPage]               = useState(0);
  const [viewAll, setViewAll]         = useState(false);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const filterScrollRef = useRef(null);

  const priceRanges = {
    'standard':      { min: 0,    max: 1000  },
    'premium':       { min: 1000, max: 5000  },
    'super-premium': { min: 5000, max: 60000 },
  };

  const theme = THEME[id] || THEME['standard'];

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => { applyTypeFilter(allProducts, activeType); setPage(0); }, [activeType, allProducts]);

  // Check scroll state whenever filters render
  const checkScroll = () => {
    const el = filterScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    el.scrollLeft = 0; // start at beginning so All (default) is visible first
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => { el.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [filtered]);

  useEffect(() => {
    if (activeType === 'ALL') return; // keep top on startup and avoid auto-scroll to All
    const el = filterScrollRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(`[data-type="${activeType}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }, [activeType]);

  const scrollFilters = (dir) => {
    const el = filterScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const next = Math.max(0, Math.min(maxScroll, el.scrollLeft + dir * 160));
    el.scrollTo({ left: next, behavior: 'smooth' });
  };

  const fetchRatings = async () => {
    try {
      const res = await fetch(`${API}/api/products/feedback/all`);
      if (!res.ok) return;
      const allFeedback = await res.json();
      if (!Array.isArray(allFeedback)) return;
      const map = {};
      allFeedback.forEach(f => {
        if (!map[f.productId]) map[f.productId] = [];
        map[f.productId].push(f.rating);
      });
      const ratMap = {};
      Object.entries(map).forEach(([pid, ratings]) => {
        ratMap[Number(pid)] = { avg: ratings.reduce((s, r) => s + r, 0) / ratings.length, count: ratings.length };
      });
      setRatingsMap(ratMap);
    } catch {}
  };

  const fetchProducts = async () => {
    try {
      setLoading(true); setError(false);
      const response = await fetch(`${API}/api/products`);
      if (!response.ok) { setError(true); setLoading(false); return; }
      const data = await response.json();
      if (!Array.isArray(data)) { setFiltered([]); setLoading(false); return; }
      const fixed = data.map(p => ({
        ...p,
        mainImage: p.mainImage && !p.mainImage.startsWith('http') ? `${API}${p.mainImage}` : p.mainImage
      }));
      const categoryMap = { 'standard': 'STANDARD', 'premium': 'PREMIUM', 'super-premium': 'SUPER_PREMIUM' };
      const base = fixed.filter(p => p.category === categoryMap[id]);
      setAllProducts(base);
      applyTypeFilter(base, activeType);
      setLoading(false);
      fetchRatings();
    } catch {
      setError(true); setFiltered([]); setLoading(false);
    }
  };

  const applyTypeFilter = (base, type) => {
    setFiltered(type === 'ALL' ? base : base.filter(p => p.type === type));
  };

  const handlePriceFilter = (min, max) => {
    const base = activeType === 'ALL' ? allProducts : allProducts.filter(p => p.type === activeType);
    if (min === null && max === null) { setFiltered(base); return; }
    setFiltered(base.filter(p => {
      if (min !== null && max !== null) return p.price >= min && p.price <= max;
      if (min !== null) return p.price >= min;
      if (max !== null) return p.price <= max;
      return true;
    }));
    setPage(0);
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const displayed  = viewAll ? filtered : filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const arrowBtn = (onClick, disabled, label) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
        border: `1.5px solid ${disabled ? '#e0e0e0' : theme.active}`,
        background: disabled ? '#f5f5f5' : 'white',
        color: disabled ? '#ccc' : theme.active,
        cursor: disabled ? 'default' : 'pointer',
        fontWeight: '700', fontSize: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s', lineHeight: 1,
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = theme.active; e.currentTarget.style.color = '#fff'; }}}
      onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = theme.active; }}}
    >{label}</button>
  );

  return (
    <section id={id} className={`product-section ${bgClass}`}>
      <div className="container">

        {/* Row 1 on desktop: Title (left) + filter pills (right). On mobile: stacked */}
        <div className="ps-header-row">

          {/* Title + tagline stacked on the left */}
          <div className="ps-title-block">
            <h2 className={titleClass} style={{ margin: 0 }}>{title}</h2>
            <p className="section-tagline" style={{ margin: '2px 0 0 0' }}>{tagline}</p>
          </div>

          {/* Right side: pills → arrows (centered) → view all (centered) */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
            {/* Scrollable pills row */}
            <div
              ref={filterScrollRef}
              className="ps-pills-row"
              style={{
                overflowX: 'auto',
                minWidth: 0,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {TYPE_LABELS.map(t => {
                const isActive = activeType === t.key;
                return (
                  <button
                    key={t.key}
                    data-type={t.key}
                    onClick={() => setActiveType(t.key)}
                    style={{
                      padding: '5px 14px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0,
                      border: `1.5px solid ${isActive ? theme.active : '#ddd'}`,
                      background: isActive ? theme.active : '#fff',
                      color: isActive ? '#fff' : '#555',
                      fontWeight: isActive ? '700' : '500',
                      fontSize: '12px', cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? `0 2px 8px ${theme.faint.replace('0.1', '0.3')}` : 'none',

                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = theme.faint; e.currentTarget.style.borderColor = theme.active; e.currentTarget.style.color = theme.active; }}}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#ddd'; e.currentTarget.style.color = '#555'; }}}
                  >{t.label}</button>
                );
              })}
            </div>
            {/* Arrows centered below pills */}
            <div className="ps-arrows-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              {arrowBtn(() => scrollFilters(-1), false, '‹')}
              {arrowBtn(() => scrollFilters(1), false, '›')}
            </div>
            {/* View All centered below arrows */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <a
                href={`/products?category=${id}`}
                className="ps-view-all"
                style={{ color: theme.active }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >View All →</a>
            </div>
          </div>
        </div>

        <SimplePriceFilter onFilterChange={handlePriceFilter} minPrice={priceRanges[id]?.min || 0} maxPrice={priceRanges[id]?.max || 1000} />

        <div className="product-grid">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', gridColumn: '1 / -1' }}>
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', gridColumn: '1 / -1' }}>
              <p>⚠️ Backend service not running. Please start Product Service on port 8082.</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>Run: <code>cd product-service && mvn spring-boot:run</code></p>
            </div>
          ) : displayed.length > 0 ? (
            displayed.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  <img src={product.mainImage || 'images/product.jpg'} alt={product.name} onError={e => { e.target.src = 'images/product.jpg'; }} />
                </div>
                <h3 className="product-name">{product.name}</h3>
                <p style={{ fontSize: '12px', color: '#666', margin: '5px 0', minHeight: '40px' }}>{product.description}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '4px 0' }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ fontSize: '15px', color: ratingsMap[product.id] && i < Math.round(ratingsMap[product.id].avg) ? '#ffc107' : '#ddd' }}>★</span>
                  ))}
                  {ratingsMap[product.id] ? (
                    <span style={{ fontSize: '12px', color: '#888' }}>{ratingsMap[product.id].avg.toFixed(1)} ({ratingsMap[product.id].count})</span>
                  ) : (
                    <span className="no-ratings-text" style={{ fontSize: '12px', color: '#bbb' }}>No ratings yet</span>
                  )}
                </div>
                <p style={{ fontSize: '16px', fontWeight: '700', color: theme.active, margin: '10px 0' }}>₹{product.price}</p>
                <button className="view-btn" onClick={() => onProductClick(product.id)}>View</button>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', gridColumn: '1 / -1' }}>
              <p style={{ color: '#666', fontSize: '15px', marginBottom: '10px' }}>
                No products found in the <strong>{id === 'standard' ? 'Standard' : id === 'premium' ? 'Premium' : 'Super Premium'}</strong> category for this selection.
              </p>
              <p style={{ color: '#888', fontSize: '13px' }}>
                Please explore our{' '}
                {id !== 'standard' && <a href="/#standard" style={{ color: '#107FDF', fontWeight: '600', textDecoration: 'underline' }}>Standard</a>}
                {id !== 'standard' && id !== 'premium' && ' or '}
                {id !== 'premium' && <a href="/#premium" style={{ color: '#0A683C', fontWeight: '600', textDecoration: 'underline' }}>Premium</a>}
                {id !== 'super-premium' && ' or '}
                {id !== 'super-premium' && <a href="/#super-premium" style={{ color: '#9A1111', fontWeight: '600', textDecoration: 'underline' }}>Super Premium</a>}
                {' '}range for more suitable options.
              </p>
            </div>
          )}
        </div>

        {/* Pagination arrows below grid */}
        {!viewAll && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
            {arrowBtn(() => setPage(p => Math.max(0, p - 1)), page === 0, '‹')}
            {arrowBtn(() => setPage(p => Math.min(totalPages - 1, p + 1)), page >= totalPages - 1, '›')}
          </div>
        )}

      </div>
    </section>
  );
}

export default ProductSection;
